// Authentication Context for global state management
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as authService from '../services/authService';
import { trackMetaEvent } from '../services/metaConversionsService';
import { CONVERSIONS, trackConversion } from '../services/conversions';

export const AuthContext = createContext(null);

const AUTH_STORAGE_KEY = 'auth_token';
const CACHED_USER_KEY = 'cached_user';
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const INACTIVITY_POLL = 60 * 1000; // check once a minute

const getCachedUser = () => {
  try {
    const token = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!token) return null;
    const raw = sessionStorage.getItem(CACHED_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const setCachedUser = (user) => {
  try {
    if (user) sessionStorage.setItem(CACHED_USER_KEY, JSON.stringify(user));
    else sessionStorage.removeItem(CACHED_USER_KEY);
  } catch {}
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => getCachedUser());
  const [loading, setLoading] = useState(!!localStorage.getItem(AUTH_STORAGE_KEY));
  const [token, setToken] = useState(() => localStorage.getItem(AUTH_STORAGE_KEY));
  const [loginModal, setLoginModal] = useState({ open: false, mode: 'login' });

  /* Activity lives in a ref, not state. It is written by scroll/mousedown/
     keydown/touchstart — hundreds of times a minute — and nothing renders from
     it, so state here would re-render every consumer of this context (i.e. the
     whole app) on every scroll frame. */
  const lastActivity = useRef(Date.now());

  /* The inactivity timer calls logout, and logout is redefined every render.
     Reading it through a ref keeps the timer effect off that dependency, so it
     is installed once per session instead of being torn down and rebuilt. */
  const logoutRef = useRef(null);

  // Initialize auth state from stored token
  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      const storedToken = localStorage.getItem(AUTH_STORAGE_KEY);

      if (storedToken) {
        try {
          const userData = await authService.verifyToken(storedToken);
          if (cancelled) return;
          setUser(userData);
          setToken(storedToken);
          setCachedUser(userData);
        } catch {
          if (cancelled) return;
          // Expired or tampered token — drop it and continue as a guest.
          localStorage.removeItem(AUTH_STORAGE_KEY);
          setCachedUser(null);
          setUser(null);
          setToken(null);
        }
      }

      if (!cancelled) setLoading(false);
    };

    initAuth();
    return () => { cancelled = true; };
  }, []);

  // Auto-logout on inactivity
  useEffect(() => {
    if (!user) return undefined;

    lastActivity.current = Date.now();

    const updateActivity = () => { lastActivity.current = Date.now(); };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    // passive: these listeners only stamp a timestamp, so the browser never has
    // to wait on them before scrolling.
    events.forEach((e) => window.addEventListener(e, updateActivity, { passive: true }));

    const interval = setInterval(() => {
      if (Date.now() - lastActivity.current > INACTIVITY_TIMEOUT) logoutRef.current?.();
    }, INACTIVITY_POLL);

    return () => {
      clearInterval(interval);
      events.forEach((e) => window.removeEventListener(e, updateActivity));
    };
  }, [user]);

  const handleLogin = useCallback(async (email, password) => {
    try {
      const { user: userData, token: authToken } = await authService.login(email, password);
      setUser(userData);
      setToken(authToken);
      localStorage.setItem(AUTH_STORAGE_KEY, authToken);
      setCachedUser(userData);
      lastActivity.current = Date.now();
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const handleRegister = useCallback(async (userData) => {
    try {
      const { user: newUser, token: authToken } = await authService.register(userData);
      setUser(newUser);
      setToken(authToken);
      localStorage.setItem(AUTH_STORAGE_KEY, authToken);
      setCachedUser(newUser);
      lastActivity.current = Date.now();
      trackMetaEvent('CompleteRegistration');
      trackConversion(CONVERSIONS.signUp, { method: 'email' });
      return { success: true, user: newUser };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const handleSocialAuth = useCallback(async (provider, socialToken, completionData = null) => {
    try {
      const result = await authService.socialAuth(provider, socialToken, completionData);

      if (result.isNewUser) {
        return { isNewUser: true, providerProfile: result.providerProfile };
      }

      const { user: userData, token: authToken } = result;
      setUser(userData);
      setToken(authToken);
      localStorage.setItem(AUTH_STORAGE_KEY, authToken);
      setCachedUser(userData);
      lastActivity.current = Date.now();
      // With completion data this call created the account; without, it was a sign-in.
      if (completionData) {
        trackMetaEvent('CompleteRegistration');
        trackConversion(CONVERSIONS.signUp, { method: String(provider).toLowerCase() });
      }
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      if (token) {
        await authService.logout(token);
      }
      setUser(null);
      setToken(null);
      localStorage.removeItem(AUTH_STORAGE_KEY);
      setCachedUser(null);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [token]);

  useEffect(() => { logoutRef.current = handleLogout; }, [handleLogout]);

  const handleUpdateProfile = useCallback(async (updates) => {
    if (!token) return { success: false, error: 'Not authenticated' };

    try {
      const updatedUser = await authService.updateProfile(token, updates);
      setUser(updatedUser);
      setCachedUser(updatedUser);
      return { success: true, user: updatedUser };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [token]);

  const handleChangePassword = useCallback(async (currentPassword, newPassword) => {
    if (!token) return { success: false, error: 'Not authenticated' };

    try {
      await authService.changePassword(token, currentPassword, newPassword);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [token]);

  const handlePasswordReset = useCallback(async (email) => {
    try {
      await authService.requestPasswordReset(email);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const handleDeleteAccount = useCallback(async (confirmationEmail) => {
    if (!token) return { success: false, error: 'Not authenticated' };
    try {
      await authService.deleteAccount(token, confirmationEmail);
      setUser(null);
      setToken(null);
      localStorage.removeItem(AUTH_STORAGE_KEY);
      setCachedUser(null);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [token]);

  const openLogin = useCallback((mode = 'login') => {
    setLoginModal({ open: true, mode: mode === 'signup' ? 'signup' : 'login' });
  }, []);

  const closeLogin = useCallback(() => {
    setLoginModal((prev) => ({ ...prev, open: false }));
  }, []);

  /* Memoized so consumers only re-render when the auth state itself changes,
     not on every render of this provider. */
  const value = useMemo(() => ({
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login: handleLogin,
    register: handleRegister,
    socialAuth: handleSocialAuth,
    logout: handleLogout,
    updateProfile: handleUpdateProfile,
    changePassword: handleChangePassword,
    requestPasswordReset: handlePasswordReset,
    deleteAccount: handleDeleteAccount,
    loginModal,
    openLogin,
    closeLogin,
  }), [
    user, token, loading, loginModal,
    handleLogin, handleRegister, handleSocialAuth, handleLogout,
    handleUpdateProfile, handleChangePassword, handlePasswordReset,
    handleDeleteAccount, openLogin, closeLogin,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
